import {isEmpty, map, uniqueId, omit} from 'lodash';
import React from 'react';
import {Button} from 'acud';
import {ButtonProps} from 'acud/lib/button';

import {useLocale} from '../../../components/locale';

export type TActionType = 'link' | 'ajax' | 'dialog' | 'drawer' | 'custom';

export interface CommandParams {
    config: any;
    payload?: any;
}

export interface BulkItemConfig extends ButtonProps {
    id?: string;
    text?: React.ReactNode;
    skin?: 'primary' | 'text' | 'highlight' | 'enhance' | 'default' | 'danger';
    select?: boolean; // 是否和表格批量选择有关
    actionType?: TActionType;
    link?: string;
    ajax?: object;
    dialog?: object;
    drawer?: object;
    action?: string;
}

export interface BulkProps {
    prefixCls?: string;
    bulkComp?: React.ComponentType<any>;
    bulkConfig?: any[];
    selectedRowKeys?: any[];
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
}

export interface SelectedProps {
    prefixCls?: string;
    selectedRowKeys?: any[];
    total?: number;
}

export interface IFilterConfig {
    controls?: any[];
}

export interface FilterProps {
    prefixCls?: string;
    filterComp?: React.ComponentType<any>;
    filterConfig?: IFilterConfig;
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
}

export interface ToolbarProps {
    prefixCls?: string;
    withToolbar?: boolean;
    bulkProps?: BulkProps;
    selectedProps?: SelectedProps;
    filterProps?: FilterProps;
    handleCommand?: (commandParams: CommandParams) => void;
    handleFilterChange?: (filterItemData: any) => void;
}

export const renderButton = (props: any) => {
    const {disabled, skin, text, handleCommand, customRender, ...buttonConfig} = props;
    const type = skin === 'danger' ? 'highlight' : skin;

    const btnPanel = (
        <Button
            key={uniqueId()}
            type={type}
            disabled={disabled}
            {...omit(buttonConfig, ['actionType', 'select'])}
            onClick={() => handleCommand?.({config: props})}
        >
            {text}
        </Button>
    );
    return customRender?.(btnPanel, props) || btnPanel;
};

const Bulk = (props: BulkProps) => {
    const {prefixCls, bulkComp: BulkComp, bulkConfig, selectedRowKeys, handleCommand, handleFilterChange} = props;

    const hasSelected = (selectedRowKeys ?? []).length > 0;

    if (BulkComp) {
        return (
            <BulkComp
                selectedRowKeys={selectedRowKeys}
                hasSelected={hasSelected}
                handleCommand={handleCommand}
                handleFilterChange={handleFilterChange}
            />
        );
    }

    return (
        <div className={`${prefixCls}-hooks-table-toolbar-bulk`}>
            {isEmpty(bulkConfig) ? null : map(bulkConfig, configItem => {
                const disabled = configItem.disabled || (configItem.select && !hasSelected);
                return renderButton({...configItem, disabled, handleCommand});
            })}
        </div>
    );
};

const Selected = (props: SelectedProps) => {
    const [locale] = useLocale('HooksTable');

    const {prefixCls, selectedRowKeys, total} = props;

    const selected = (selectedRowKeys ?? []).length;

    if (!selected) {
        return <></>;
    }
    return (
        <span className={`${prefixCls}-hooks-table-selected-count`}>
            {locale.selected} {selected} {locale.item}{locale.total} {total} {locale.item2}
        </span>
    );
};

const Filter = (props: FilterProps) => {
    const {prefixCls, filterComp: FilterComp, filterConfig, handleCommand, handleFilterChange} = props;

    if (FilterComp) {
        return <FilterComp handleFilterChange={handleFilterChange} />;
    }

    const controls = filterConfig?.controls;

    if (isEmpty(filterConfig) || isEmpty(controls)) {
        return null;
    }

    return (
        <div className={`${prefixCls}-hooks-table-toolbar-filter`}>
            {map(controls, (controlItem, idx) => {
                if (controlItem.actionType) {
                    return renderButton({...controlItem, handleCommand});
                }
                const {key, name, label, control: Control, controlProps} = controlItem;
                return Control ? (
                    <div key={idx}>
                        {label && <label>{label}</label>}
                        <Control key={key} name={name} {...controlProps} />
                    </div>
                ) : null;
            })}
        </div>
    );
};

const ToolBar = (props: ToolbarProps) => {
    const {prefixCls, withToolbar, bulkProps, selectedProps, filterProps, handleCommand, handleFilterChange} = props;

    return withToolbar ? (
        <div className={`${prefixCls}-hooks-table-toolbar`}>
            <div className={`${prefixCls}-hooks-table-toolbar-left`}>
                <Bulk
                    prefixCls={prefixCls}
                    {...bulkProps}
                    selectedRowKeys={selectedProps?.selectedRowKeys}
                    handleCommand={handleCommand}
                    handleFilterChange={handleFilterChange}
                />
                <Selected prefixCls={prefixCls} {...selectedProps} />
            </div>
            <Filter
                prefixCls={prefixCls}
                {...filterProps}
                handleCommand={handleCommand}
                handleFilterChange={handleFilterChange}
            />
        </div>
    ) : null;
};

export default ToolBar;
